import {
  BookOpen,
  Clock,
  FilePlus2,
  FileSearch,
  LineChart,
  ListChecks,
  PhoneCall,
  PlayCircle,
  Search,
  UserPlus,
} from 'lucide-react';
import { useRouterState } from '@tanstack/react-router';
import { describePage } from './pageContext';
import type { LucideIcon } from 'lucide-react';

/**
 * Page-aware prompt suggestions.
 *
 * The starter prompts used to be one hardcoded, strategy-flavoured list shown
 * everywhere — so an advisor on /clients had no hint that Soteria can manage
 * their book at all. Now that one assistant covers every domain, the openers
 * should reflect the page you're on; it's the cheapest way to make that reach
 * discoverable.
 *
 * ONE RULE: only suggest what a specialist can actually deliver. A pill that
 * leads to "I can't do that yet" is worse than no pill, especially now that
 * the supervisor is told to decline rather than improvise. Routes with no
 * specialist behind them (portfolio, execution, team…) get the generic set
 * rather than invented promises.
 */
export interface PromptSuggestion {
  /** Short pill text. */
  label: string;
  /** What actually gets sent — a real question, not the label. */
  prompt: string;
  icon: LucideIcon;
}

// Backed by platform_docs / knowledge_base / the two specialists, so these are
// safe anywhere — including pages no specialist owns yet.
const GENERIC: Array<PromptSuggestion> = [
  {
    label: 'Build a strategy',
    prompt: 'Build me a strategy',
    icon: LineChart,
  },
  {
    label: 'Find a client',
    prompt: 'Find a client in my book',
    icon: Search,
  },
  {
    label: 'How onboarding works',
    prompt: 'How does opening a client account work on this platform?',
    icon: BookOpen,
  },
];

const CLIENT_DIRECTORY: Array<PromptSuggestion> = [
  {
    label: 'Add a client',
    prompt: 'Add a new client',
    icon: UserPlus,
  },
  {
    label: "Who's gone cold",
    prompt: "Which clients haven't I been in contact with recently?",
    icon: Clock,
  },
  {
    label: 'Open follow-ups',
    prompt: "What follow-ups are open across the team's book?",
    icon: ListChecks,
  },
  {
    label: 'Find a client',
    prompt: 'Find a client in my book',
    icon: Search,
  },
];

// On a client's page the assistant resolves "this client" from the page
// context, so the prompts don't need to name them.
const CLIENT_DETAIL: Array<PromptSuggestion> = [
  {
    label: 'Prep me for a meeting',
    prompt: 'Prepare me for my next meeting with this client',
    icon: Clock,
  },
  {
    label: 'Log a call',
    prompt: 'Log a call with this client',
    icon: PhoneCall,
  },
  {
    label: 'Add a follow-up',
    prompt: 'Add a follow-up on this client',
    icon: ListChecks,
  },
  {
    label: 'Open an account',
    prompt: 'Open an account for this client',
    icon: FilePlus2,
  },
  {
    label: "What's still needed",
    prompt:
      'What details are still missing before I can open an account for this client?',
    icon: FileSearch,
  },
];

const STRATEGIES: Array<PromptSuggestion> = [
  {
    label: 'Build a strategy',
    prompt: 'Build me a strategy',
    icon: LineChart,
  },
  {
    label: 'My current strategy',
    prompt: "What's in my current strategy?",
    icon: FileSearch,
  },
  {
    label: 'Run a backtest',
    prompt: 'Run a backtest on my current strategy',
    icon: PlayCircle,
  },
];

const STRATEGY_REPORT: Array<PromptSuggestion> = [
  {
    label: 'Explain the results',
    prompt: 'Explain how this backtest performed',
    icon: FileSearch,
  },
  {
    label: 'Why this result?',
    prompt: 'Why did this strategy perform the way it did?',
    icon: LineChart,
  },
];

// Keyed by the `area` describePage() reports, so this stays in step with the
// route matcher instead of re-parsing paths.
const BY_AREA: Record<string, Array<PromptSuggestion>> = {
  'Client directory': CLIENT_DIRECTORY,
  'Client detail': CLIENT_DETAIL,
  'Account opening wizard': CLIENT_DETAIL,
  'Strategies list': STRATEGIES,
  'Strategy builder': STRATEGIES,
  'Strategy backtest report': STRATEGY_REPORT,
  'Live model': STRATEGY_REPORT,
};

/** Starter prompts for a pathname. Falls back to the generic set. */
export function suggestionsForPage(pathname: string): Array<PromptSuggestion> {
  return BY_AREA[describePage(pathname).area] ?? GENERIC;
}

/** Starter prompts for the route the user is on right now. */
export function usePromptSuggestions(): Array<PromptSuggestion> {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return suggestionsForPage(pathname);
}
